// ─── DataAnalyticsView ────────────────────────────────────────────────────────
// Top-level container for the Dashboard → Data Analytics workspace.
// Renders the sub-tab header (Visualize / Validate / Formulas with the
// XD green underline) plus the shared date-range controls, then dispatches
// to the active sub-tab component.
//
// This component is intentionally thin — all sub-tab implementation lives
// in sibling files so each XD screen has its own focused module.
// ──────────────────────────────────────────────────────────────────────────────

import VisualizeTab from './VisualizeTab'
import ValidateTab from './ValidateTab'
import FormulasTab from './FormulasTab'

const SUB_TABS = ['Visualize', 'Validate', 'Formulas']

export default function DataAnalyticsView({
    currentDataAnalyticsIndex,
    setCurrentDataAnalyticsIndex,
    dateRangeGraph,
    setDateRangeGraph,
    activePeriod,
    handlePeriodClick,
    selectedParameters = [],
}) {
    return (
        <div className="w-full h-full flex flex-col bg-white">
            {/* Sub-tab header with date controls */}
            <div className="flex items-center justify-between border-b border-gray-200 px-4 pt-4 pb-3 mb-3">
                <div className="flex gap-8">
                    {SUB_TABS.map((label, index) => {
                        const active = index === currentDataAnalyticsIndex
                        return (
                            <button
                                key={label}
                                type="button"
                                onClick={() => setCurrentDataAnalyticsIndex(index)}
                                className={`text-sm font-medium pb-2 border-b-2 transition ${
                                    active
                                        ? 'border-[#5EA64D] text-[#5EA64D]'
                                        : 'border-transparent text-gray-700 hover:text-gray-900'
                                }`}
                            >
                                {label}
                            </button>
                        )
                    })}
                </div>

                {/* Right: D/W/M/Y + Start / End date */}
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 border border-gray-300 rounded px-1 py-0.5">
                        {['D', 'W', 'M', 'Y'].map((p) => (
                            <button
                                key={p}
                                type="button"
                                onClick={() => handlePeriodClick(p)}
                                className={`px-2 py-0.5 text-xs font-medium rounded transition ${
                                    activePeriod === p
                                        ? 'bg-gray-700 text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                        <label className="flex flex-col text-[10px] text-gray-500">
                            <span>Start Date</span>
                            <input
                                type="date"
                                value={dateRangeGraph?.from?.split(' ')[0] || ''}
                                onChange={(e) =>
                                    setDateRangeGraph((prev) => ({ ...prev, from: e.target.value + ' 00:00:00' }))
                                }
                                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-[#358ECD]"
                            />
                        </label>
                        <label className="flex flex-col text-[10px] text-gray-500">
                            <span>End Date</span>
                            <input
                                type="date"
                                value={dateRangeGraph?.to?.split(' ')[0] || ''}
                                onChange={(e) =>
                                    setDateRangeGraph((prev) => ({ ...prev, to: e.target.value + ' 23:59:59' }))
                                }
                                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-[#358ECD]"
                            />
                        </label>
                    </div>
                </div>
            </div>

            {/* Active sub-tab body */}
            {currentDataAnalyticsIndex === 0 && (
                <VisualizeTab
                    selectedParameters={selectedParameters}
                    dateRangeGraph={dateRangeGraph}
                    activePeriod={activePeriod}
                />
            )}
            {currentDataAnalyticsIndex === 1 && (
                <ValidateTab
                    selectedParameters={selectedParameters}
                    dateRangeGraph={dateRangeGraph}
                    activePeriod={activePeriod}
                />
            )}
            {currentDataAnalyticsIndex === 2 && (
                <FormulasTab
                    selectedParameters={selectedParameters}
                    dateRangeGraph={dateRangeGraph}
                    activePeriod={activePeriod}
                />
            )}
        </div>
    )
}
